/**
 * Pure helpers for the declarative customer skin renderer.
 *
 * Everything here works from the resolved effective skin returned by the API;
 * no organisation names, ids, brand colours or guide measurements live in
 * renderer code. Colours, typography, logo presentation and decorative
 * geometry all arrive as configuration.
 */
import type {
  CustomerSkinEffective,
  CustomerSkinPalette,
  CustomerSkinTheme,
  CustomerSkinTypography
} from '@cdf/api-client-react';

import { mapTokenVariables } from './customer-skin-map';

export type SkinThemeName = 'light' | 'dark';
export { MAP_PLATFORM_DEFAULTS, mapCssVariable, mapTokenPaths, resolvedMapColours, tenantMapTokens } from './customer-skin-map';
export type { MapTokenPath, ResolvedMapColours } from './customer-skin-map';

const hexPattern = /^#([0-9a-fA-F]{6})$/;

/** sRGB channels (0-255) for a six-digit hex colour; null when unparseable. */
export function hexChannels(value: string | null | undefined): { r: number; g: number; b: number } | null {
  if (!value) return null;
  const match = hexPattern.exec(value.trim());
  if (!match) return null;
  const parsed = Number.parseInt(match[1] ?? '', 16);
  if (!Number.isFinite(parsed)) return null;
  return { r: (parsed >> 16) & 0xff, g: (parsed >> 8) & 0xff, b: parsed & 0xff };
}

function channelLuminance(channel: number): number {
  const component = channel / 255;
  return component <= 0.04045 ? component / 12.92 : ((component + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance for a six-digit hex colour; null when unparseable. */
export function colourLuminance(value: string | null | undefined): number | null {
  const channels = hexChannels(value);
  if (!channels) return null;
  return (
    channelLuminance(channels.r) * 0.2126 +
    channelLuminance(channels.g) * 0.7152 +
    channelLuminance(channels.b) * 0.0722
  );
}

/** WCAG contrast ratio between two six-digit hex colours; null when either is unparseable. */
export function contrastRatio(a: string | null | undefined, b: string | null | undefined): number | null {
  const first = colourLuminance(a);
  const second = colourLuminance(b);
  if (first === null || second === null) return null;
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/** White or black, whichever reads best on the supplied colour; null when unparseable. */
export function readableForeground(value: string): string | null {
  const luminance = colourLuminance(value);
  if (luminance === null) return null;
  const white = 1.05 / (luminance + 0.05);
  const black = (luminance + 0.05) / 0.05;
  return white >= black ? '#ffffff' : '#000000';
}

/** A subtle hover surface that never reduces text contrast below AA. */
function navigationHover(background: string, foreground: string): string {
  const backgroundChannels = hexChannels(background);
  const foregroundChannels = hexChannels(foreground);
  if (!backgroundChannels || !foregroundChannels) return background;
  const channels = (['r', 'g', 'b'] as const).map(key =>
    Math.round(backgroundChannels[key] * 0.9 + foregroundChannels[key] * 0.1).toString(16).padStart(2, '0'));
  const candidate = '#' + channels.join('');
  return (contrastRatio(candidate, foreground) ?? 0) >= 4.5 ? candidate : background;
}

/** True when a surface is dark enough that white text is the readable choice. */
export function isDarkSurface(value: string | null | undefined): boolean {
  const luminance = colourLuminance(value);
  if (luminance === null) return false;
  return 1.05 / (luminance + 0.05) >= 4.5;
}

/** Active theme values for the resolved skin. */
export function skinTheme(skin: CustomerSkinEffective | null, theme: SkinThemeName): CustomerSkinTheme {
  if (!skin) return {};
  return (theme === 'dark' ? skin.dark : skin.light) ?? {};
}

/** Bundled font stack for the configured typography; null keeps the app default. */
export function skinFontStack(typography: CustomerSkinTypography | null | undefined): string | null {
  if (typography?.font !== 'lato') return null;
  const fallback =
    typography.fallback === 'system'
      ? 'ui-sans-serif, system-ui, sans-serif'
      : "Calibri, 'Segoe UI', ui-sans-serif, system-ui, sans-serif";
  return `'Lato', ${fallback}`;
}

/** Areas the brand may apply to (docs/requirements/design-system-import.md §8). */
export const skinScopeFlags = [
  'header', 'navigation', 'controls', 'cards', 'decorations', 'mobile', 'maps', 'publicPages', 'browserIdentity', 'emails'
] as const;
export type SkinScopeFlag = typeof skinScopeFlags[number];
export type BrandScope = { [K in SkinScopeFlag]: K extends 'emails' ? boolean | null : boolean };

/** Defaults preserve today's behaviour; `emails: null` follows the email style setting. */
export const BRAND_SCOPE_DEFAULTS: BrandScope = {
  header: true, navigation: true, controls: true, cards: true, decorations: true, mobile: true, maps: true,
  publicPages: false, browserIdentity: false, emails: null
};

/** Resolved brand scope; flags the server did not send keep their platform default. */
export function brandScope(skin: Pick<CustomerSkinEffective, 'applyScope'> | null | undefined): BrandScope {
  const scope: BrandScope = { ...BRAND_SCOPE_DEFAULTS };
  const stored = skin?.applyScope;
  if (!stored) return scope;
  for (const flag of skinScopeFlags) {
    const value = stored[flag];
    if (typeof value === 'boolean') scope[flag] = value;
  }
  return scope;
}

export interface SkinTokenInput {
  skin?: CustomerSkinEffective | null;
  theme: SkinThemeName;
  themeValues: CustomerSkinTheme;
  palette: CustomerSkinPalette;
  header: { background: string; foreground: string } | null;
  fontFamily: string | null;
  /** Where the tokens render; public pages apply the skin only when `publicPages` is on. */
  surface?: 'signed-in' | 'public';
  /** Phone-width viewports apply the skin only when `mobile` is on. */
  viewport?: 'phone' | 'wide';
}

const HEADING_WEIGHTS = { regular: '400', bold: '700', heavy: '800' } as const;
const EASINGS = { standard: 'cubic-bezier(0.2, 0, 0, 1)', emphasized: 'cubic-bezier(0.3, 0, 0, 1)', linear: 'linear' } as const;

/** Typography, radius, state, motion and icon tokens owned by the controls scope. */
function controlScaleTokens(skin: CustomerSkinEffective | null | undefined, tokens: Record<string, string>): void {
  const scale = skin?.typography?.scale;
  if (scale) {
    for (const [step, value] of Object.entries(scale)) {
      if (value?.size != null) tokens[`--customer-text-${step}`] = `${value.size}rem`;
      if (value?.lineHeight != null) tokens[`--customer-text-${step}--line-height`] = String(value.lineHeight);
    }
  }
  const headingWeight = skin?.typography?.headingWeight;
  if (headingWeight) tokens['--customer-heading-weight'] = HEADING_WEIGHTS[headingWeight];
  const radius = skin?.surfaces?.radius;
  if (radius) {
    // Tailwind's rounded-md is calc(--radius - 2px), so md alone derives sm/lg/xl as md-2/md+2/md+6.
    if (radius.md != null) tokens['--radius'] = `${radius.md + 2}px`;
    for (const step of ['sm', 'md', 'lg', 'xl', 'full'] as const) {
      if (radius[step] != null) tokens[`--customer-radius-${step}`] = `${radius[step]}px`;
    }
  }
  const state = skin?.state;
  if (state) {
    if (state.hoverOpacity != null) tokens['--state-hover-opacity'] = String(state.hoverOpacity);
    if (state.pressedOpacity != null) tokens['--state-pressed-opacity'] = String(state.pressedOpacity);
    if (state.disabledOpacity != null) tokens['--state-disabled-opacity'] = String(state.disabledOpacity);
    if (state.focusRingWidth != null) tokens['--focus-ring-width'] = `${state.focusRingWidth}px`;
    if (state.focusRingOffset != null) tokens['--focus-ring-offset'] = `${state.focusRingOffset}px`;
  }
  const motion = skin?.motion;
  if (motion) {
    if (motion.durationFast != null) tokens['--customer-duration-fast'] = `${motion.durationFast}ms`;
    if (motion.durationBase != null) tokens['--customer-duration-base'] = `${motion.durationBase}ms`;
    if (motion.easing) tokens['--customer-easing'] = EASINGS[motion.easing];
  }
  if (skin?.icons?.strokeWidth != null) tokens['--customer-icon-stroke'] = String(skin.icons.strokeWidth);
}

/** Background/foreground pair; the foreground is derived only when absent. */
function pairTokens(
  tokens: Record<string, string>,
  background: string | null | undefined,
  foreground: string | null | undefined,
  backgroundNames: readonly string[],
  foregroundNames: readonly string[]
): void {
  const resolvedForeground = foreground ?? (background ? readableForeground(background) : null);
  if (background) for (const name of backgroundNames) tokens[name] = background;
  if (resolvedForeground) for (const name of foregroundNames) tokens[name] = resolvedForeground;
}

/**
 * Semantic tokens for the document root. The resolved skin wins; the legacy
 * primary pair stays as fallback so customers without a skin keep their
 * current appearance.
 */
export function skinTokens(
  input: SkinTokenInput,
  legacy: { primary?: string | null; foreground?: string | null } = {}
): Record<string, string> {
  const scope = brandScope(input.skin);
  if (input.surface === 'public' && !scope.publicPages) return {};
  if (input.viewport === 'phone' && !scope.mobile) return {};
  const values = input.themeValues;
  const controls: Record<string, string> = {};
  const cards: Record<string, string> = {};
  const header: Record<string, string> = {};
  const navigation: Record<string, string> = {};
  const decorations: Record<string, string> = {};

  // Controls: primary, secondary, accent surface, destructive, field and focus colours.
  const primary = values.primary ?? legacy.primary ?? null;
  const foreground =
    values.onPrimary ??
    (values.primary ? readableForeground(values.primary) : null) ??
    legacy.foreground ??
    null;
  if (primary) {
    controls['--primary'] = primary;
    controls['--ring'] = primary;
  }
  if (foreground) controls['--primary-foreground'] = foreground;
  if (values.ring) controls['--ring'] = values.ring;
  if (values.input) controls['--input'] = values.input;
  // Muted keeps driving secondary and accent surfaces unless they are set explicitly.
  pairTokens(controls, values.secondary ?? values.muted, values.secondary ? values.onSecondary : values.onMuted,
    ['--secondary'], ['--secondary-foreground']);
  pairTokens(controls, values.accentSurface ?? values.muted, values.accentSurface ? values.onAccentSurface : values.onMuted,
    ['--accent'], ['--accent-foreground']);
  pairTokens(controls, values.destructive, values.onDestructive, ['--destructive'], ['--destructive-foreground']);
  if (values.accent) {
    controls['--customer-accent'] = values.accent;
    const accentForeground = readableForeground(values.accent);
    if (accentForeground) controls['--customer-accent-foreground'] = accentForeground;
  }
  if (input.fontFamily) controls['--customer-font-family'] = input.fontFamily;
  controlScaleTokens(input.skin, controls);

  // Cards: page, card and popover surfaces, muted surfaces, borders and status colours.
  pairTokens(cards, values.background, values.foreground, ['--background'], ['--foreground']);
  pairTokens(cards, values.surface, values.onSurface, ['--card', '--popover'], ['--card-foreground', '--popover-foreground']);
  pairTokens(cards, values.popover, values.popover ? values.onPopover : null, ['--popover'], ['--popover-foreground']);
  pairTokens(cards, values.muted, values.onMuted, ['--muted'], ['--muted-foreground']);
  pairTokens(cards, values.success, values.onSuccess, ['--success'], ['--success-foreground']);
  pairTokens(cards, values.warning, values.onWarning, ['--warning'], ['--warning-foreground']);
  pairTokens(cards, values.info, values.onInfo, ['--info'], ['--info-foreground']);

  const tokens = navigation;
  const navigationBackground = input.themeValues.navigationBackground ??
    (input.themeValues.navigationForeground ? readableForeground(input.themeValues.navigationForeground) : null);
  const navigationForeground = input.themeValues.navigationForeground ??
    (navigationBackground ? readableForeground(navigationBackground) : null);
  const sidebarBackground = input.themeValues.sidebarBackground ?? navigationBackground;
  // Group headings and controls sit directly on the sidebar, not the item surface.
  const sidebarForeground = sidebarBackground && input.themeValues.sidebarBackground
    ? navigationForeground && (contrastRatio(sidebarBackground, navigationForeground) ?? 0) >= 4.5
      ? navigationForeground : readableForeground(sidebarBackground)
    : navigationForeground;
  if (sidebarBackground && sidebarForeground) {
    tokens['--sidebar'] = sidebarBackground;
    tokens['--sidebar-foreground'] = sidebarForeground;
    tokens['--sidebar-accent'] = navigationHover(sidebarBackground, sidebarForeground);
    tokens['--sidebar-accent-foreground'] = sidebarForeground;
    tokens['--sidebar-ring'] = sidebarForeground;
    tokens['--sidebar-border'] = `color-mix(in srgb, ${sidebarForeground} 25%, ${sidebarBackground})`;
  }
  if (navigationBackground && navigationForeground) {
    // Legacy skins retain the inverted navigation pair for selection.
    tokens['--sidebar-primary'] = navigationForeground;
    tokens['--sidebar-primary-foreground'] = navigationBackground;
  }
  const itemBackground = input.themeValues.navigationItemBackground ??
    (input.themeValues.navigationItemForeground ? readableForeground(input.themeValues.navigationItemForeground) : null);
  const itemForeground = input.themeValues.navigationItemForeground ??
    (itemBackground ? readableForeground(itemBackground) : null);
  if (itemBackground && itemForeground) {
    tokens['--sidebar-item'] = itemBackground;
    tokens['--sidebar-item-foreground'] = itemForeground;
    tokens['--sidebar-item-accent'] = navigationHover(itemBackground, itemForeground);
    tokens['--sidebar-item-accent-foreground'] = itemForeground;
  }
  const activeBackground = input.themeValues.navigationActiveBackground ??
    (input.themeValues.navigationActiveForeground ? readableForeground(input.themeValues.navigationActiveForeground) : null);
  const activeForeground = input.themeValues.navigationActiveForeground ??
    (activeBackground ? readableForeground(activeBackground) : null);
  if (activeBackground && activeForeground) {
    tokens['--sidebar-primary'] = activeBackground;
    tokens['--sidebar-primary-foreground'] = activeForeground;
  }
  if (values.border) cards['--border'] = values.border;
  if (input.header) {
    header['--customer-header-bg'] = input.header.background;
    header['--customer-header-fg'] = input.header.foreground;
  }
  for (const entry of paletteEntries(input.palette)) {
    decorations[`--customer-palette-${entry.key}`] = entry.value;
  }
  const surfaces = input.skin?.surfaces;
  if (surfaces) {
    if (surfaces.controlRadius != null) controls['--customer-control-radius'] = surfaces.controlRadius + 'px';
    if (surfaces.cardRadius != null) cards['--customer-card-radius'] = surfaces.cardRadius + 'px';
    if (surfaces.overlayRadius != null) cards['--customer-overlay-radius'] = surfaces.overlayRadius + 'px';
    if (surfaces.density) cards['--customer-surface-spacing'] = surfaces.density === 'compact' ? '1rem' : '1.5rem';
    if (surfaces.shadow) cards['--customer-surface-shadow'] = {
      none: 'none', soft: '0 2px 8px rgb(0 0 0 / 0.10)', raised: '0 8px 24px rgb(0 0 0 / 0.18)'
    }[surfaces.shadow];
    if (surfaces.treatment) {
      cards['--customer-surface-border'] = surfaces.treatment === 'flat' ? 'transparent' : 'var(--border)';
      if (surfaces.treatment === 'elevated' && !surfaces.shadow) cards['--customer-surface-shadow'] = '0 8px 24px rgb(0 0 0 / 0.18)';
    }
  }
  return {
    ...(scope.controls ? controls : {}),
    ...(scope.cards ? cards : {}),
    ...(scope.navigation ? navigation : {}),
    ...(scope.header ? header : {}),
    ...(scope.decorations ? decorations : {}),
    ...(scope.maps ? mapTokenVariables(input.skin, input.theme, values) : {})
  };
}

/** Palette entries that carry a usable value, in declaration order. */
export function paletteEntries(
  palette: CustomerSkinPalette | null | undefined
): Array<{ key: keyof CustomerSkinPalette; value: string }> {
  if (!palette) return [];
  const entries: Array<{ key: keyof CustomerSkinPalette; value: string }> = [];
  for (const [key, value] of Object.entries(palette)) {
    if (typeof value === 'string' && value.length > 0) {
      entries.push({ key: key as keyof CustomerSkinPalette, value });
    }
  }
  return entries;
}

export interface CircleLayout {
  width: number;
  height: number;
  radius: number;
  centres: Array<{ x: number; y: number }>;
}

/** Base radius for the decorative circle device, before configuration. */
export const CIRCLE_BASE_RADIUS = 56;

/**
 * Lay out overlapping circles for the decorative device.
 *
 * `overlap` (0-100) is how much of their diameter adjacent circles share and
 * `crop` (0-80) hides that share of the circle height from the bottom, so the
 * motif keeps its thicker lower stroke while losing no more than the schema
 * allows.
 */
export function circleLayout(count: number, overlap = 50, crop = 0, radius = CIRCLE_BASE_RADIUS): CircleLayout {
  const circles = Math.min(4, Math.max(1, Math.round(count)));
  const overlapRatio = Math.min(100, Math.max(0, overlap)) / 100;
  const cropRatio = Math.min(80, Math.max(0, crop)) / 100;
  const step = 2 * radius * (1 - overlapRatio);
  const centres = Array.from({ length: circles }, (_, index) => ({
    x: radius + index * step,
    y: radius
  }));
  return {
    width: step * (circles - 1) + 2 * radius,
    height: 2 * radius * (1 - cropRatio),
    radius,
    centres
  };
}
